import { useState, useRef, useCallback, type DragEvent } from 'react';
import { observer } from 'mobx-react';
import { Button, Progress, Space, Tooltip, Typography } from 'antd';
import {
  CloudUploadOutlined,
  FilePdfOutlined,
  FileMarkdownOutlined,
  CloseOutlined,
  RedoOutlined,
  CheckCircleFilled,
  WarningFilled,
  FileUnknownOutlined,
  ExclamationCircleFilled,
  SwapOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import type { UploadTask } from '@/stores/KnowledgeDetailStore';

const { Text } = Typography;

// Big drag-drop target. Multi-file. Files auto-route to the correct upload
// endpoint by extension (PDF / Markdown / .txt). Per-file progress, retry,
// and cancel are exposed on each row. Solves the "uploading multiple files
// is cumbersome" problem from the prior UI.

export const SourceUploadDropzone = observer(function SourceUploadDropzone() {
  const { knowledgeDetailStore: kb, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      kb.enqueueUploads(files);
      trackEvent('Knowledge.UploadsEnqueued', {
        count: files.length,
        knowledgeId: kb.knowledgeId ?? -1,
      });
    },
    [kb]
  );

  const onDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    handleFiles(files);
  };

  const dropBg = isDragging
    ? isDark
      ? `linear-gradient(135deg, ${colors.ink[700]}40, ${colors.violet[700]}40)`
      : `linear-gradient(135deg, ${colors.ink[50]}, ${colors.violet[50]})`
    : isDark
    ? colors.slate[800]
    : '#FFFFFF';

  const dropBorder = isDragging
    ? colors.ink[600]
    : isDark
    ? colors.slate[600]
    : colors.slate[300];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
        }}
        style={{
          background: dropBg,
          border: `2px dashed ${dropBorder}`,
          borderRadius: 12,
          padding: '32px 20px',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 120ms ease',
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 999,
            background: isDark
              ? `${colors.ink[700]}55`
              : `linear-gradient(135deg, ${colors.ink[50]}, ${colors.violet[50]})`,
            color: colors.ink[600],
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            marginBottom: 12,
          }}
        >
          <CloudUploadOutlined />
        </div>
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: isDark ? colors.slate[100] : colors.slate[900],
            marginBottom: 4,
          }}
        >
          {isDragging ? 'Drop to upload' : 'Drag files here, or click to browse'}
        </div>
        <Text type="secondary" style={{ fontSize: 12.5 }}>
          PDF · Markdown (.md, .markdown) · Text (.txt) — multiple files supported
        </Text>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.md,.markdown,.txt"
          style={{ display: 'none' }}
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            handleFiles(files);
            e.target.value = '';
          }}
        />
      </div>

      {kb.uploads.length > 0 && <UploadPanel />}
    </div>
  );
});

// ── Summary header + per-file rows ──────────────────────────────────────────
//
// Status header is the first thing a non-tech user sees while a big drop is
// in progress. Plain language: "12 uploaded · 2 in progress · 3 already exist
// · 0 failed". No jargon, no IDs, no internal counts. The "Clear finished"
// button keeps the panel from growing forever.

const UploadPanel = observer(function UploadPanel() {
  const { knowledgeDetailStore: kb, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const summary = kb.uploadSummary;

  const segments: { label: string; count: number; tone: string }[] = [];
  if (summary.success > 0) segments.push({ label: 'uploaded', count: summary.success, tone: colors.green[600] });
  if (summary.uploading > 0) segments.push({ label: 'in progress', count: summary.uploading, tone: colors.ink[600] });
  if (summary.queued > 0) segments.push({ label: 'queued', count: summary.queued, tone: isDark ? colors.slate[300] : colors.slate[500] });
  if (summary.duplicate > 0) segments.push({ label: 'already exist', count: summary.duplicate, tone: colors.amber[600] });
  if (summary.error > 0) segments.push({ label: 'failed', count: summary.error, tone: colors.red[600] });

  const hasFinished = summary.success + summary.error + summary.duplicate > 0;

  return (
    <div
      style={{
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 10,
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <Space size={14} wrap>
          {segments.length === 0 ? (
            <Text style={{ fontSize: 12, color: isDark ? colors.slate[300] : colors.slate[600] }}>
              Preparing {summary.total} file{summary.total === 1 ? '' : 's'}…
            </Text>
          ) : (
            segments.map((s, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: s.tone }}>{s.count}</span>
                <span style={{ fontSize: 12, color: isDark ? colors.slate[400] : colors.slate[500] }}>
                  {s.label}
                </span>
              </span>
            ))
          )}
        </Space>
        {hasFinished && summary.allDone && (
          <Button size="small" type="text" onClick={() => kb.clearFinishedUploads()}>
            Clear finished
          </Button>
        )}
      </div>
      {kb.uploads.map((u) => (
        <UploadRow key={u.id} task={u} />
      ))}
    </div>
  );
});

// ── Per-file row ────────────────────────────────────────────────────────────

const UploadRow = observer(function UploadRow({ task }: { task: UploadTask }) {
  const { knowledgeDetailStore: kb, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const Icon =
    task.kind === 'pdf'
      ? FilePdfOutlined
      : task.kind === 'markdown'
      ? FileMarkdownOutlined
      : FileUnknownOutlined;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 0',
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 6,
          background:
            task.kind === 'pdf'
              ? isDark ? `${colors.ink[700]}55` : colors.ink[50]
              : task.kind === 'markdown'
              ? isDark ? `${colors.violet[700]}55` : colors.violet[50]
              : isDark ? colors.slate[700] : colors.slate[100],
          color:
            task.kind === 'pdf'
              ? colors.ink[600]
              : task.kind === 'markdown'
              ? colors.violet[600]
              : colors.slate[500],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          flexShrink: 0,
        }}
      >
        <Icon />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: isDark ? colors.slate[100] : colors.slate[900],
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {task.file.name}
        </div>
        {task.status === 'uploading' && (
          <Progress
            percent={task.percent}
            size="small"
            showInfo={false}
            strokeColor={colors.ink[600]}
            style={{ marginBottom: 0, marginTop: 2 }}
          />
        )}
        {task.status === 'queued' && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            Queued…
          </Text>
        )}
        {task.status === 'success' && (
          <Text style={{ fontSize: 11, color: colors.green[600] }}>
            Uploaded — indexing in background
          </Text>
        )}
        {task.status === 'duplicate' && (
          <Text style={{ fontSize: 11, color: colors.amber[700] }}>
            A file with this name already exists. Skip it, or replace its content.
          </Text>
        )}
        {task.status === 'error' && (
          <Tooltip title={task.error}>
            <Text style={{ fontSize: 11, color: colors.red[600] }}>
              {task.error?.slice(0, 80) ?? 'Upload failed — please try again'}
            </Text>
          </Tooltip>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {task.status === 'success' && (
          <CheckCircleFilled style={{ color: colors.green[600], fontSize: 16 }} />
        )}
        {task.status === 'duplicate' && (
          <Space size={2}>
            <ExclamationCircleFilled style={{ color: colors.amber[600], fontSize: 14, marginRight: 2 }} />
            <Tooltip title="Replace the existing file's content with this one. Tags and description are kept.">
              <Button
                type="text"
                size="small"
                icon={<SwapOutlined />}
                onClick={() => void kb.replaceExistingMarkdown(task.id)}
              >
                Replace
              </Button>
            </Tooltip>
            <Tooltip title="Skip this file (don't upload).">
              <Button
                type="text"
                size="small"
                onClick={() => kb.cancelUpload(task.id)}
              >
                Skip
              </Button>
            </Tooltip>
          </Space>
        )}
        {task.status === 'error' && (
          <Space size={2}>
            <WarningFilled style={{ color: colors.red[600], fontSize: 14 }} />
            <Tooltip title="Try uploading this file again">
              <Button
                type="text"
                size="small"
                icon={<RedoOutlined />}
                onClick={() => kb.retryUpload(task.id)}
              >
                Retry
              </Button>
            </Tooltip>
            <Tooltip title="Skip this file (don't upload).">
              <Button
                type="text"
                size="small"
                onClick={() => kb.cancelUpload(task.id)}
              >
                Skip
              </Button>
            </Tooltip>
          </Space>
        )}
        {(task.status === 'queued' || task.status === 'uploading') && (
          <Tooltip title="Cancel">
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => kb.cancelUpload(task.id)}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
});
